import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const TRIGGERS = [
  { key: "contact_created", label: "Contact created", filters: [{ k: "source", label: "Only from source (e.g. funnel:demo, booking)" }] },
  { key: "contact_scored", label: "Contact scored (AI)", filters: [] },
  { key: "contact_replied", label: "Contact replied (Inbox)", filters: [] },
  { key: "deal_stage_changed", label: "Deal stage changed", filters: [{ k: "stage", label: "Only when moved to stage" }] },
  { key: "funnel_submitted", label: "Funnel submitted", filters: [{ k: "slug", label: "Only this funnel slug" }] },
  { key: "email_opened_no_click", label: "Opened email but didn't click (24h)", filters: [{ k: "variant", label: "Only this A/B variant name (optional)" }] },
];

const ACTIONS = [
  { key: "add_tag", label: "Add tag to contact", fields: [{ k: "tag", label: "Tag" }] },
  { key: "log_note", label: "Log internal note", fields: [{ k: "content", label: "Note content", ta: true }] },
  { key: "send_email", label: "Send email (live via Resend)", fields: [{ k: "subject", label: "Subject" }, { k: "body", label: "Body", ta: true }] },
  { key: "send_ai_email", label: "Send AI-drafted email", fields: [{ k: "goal", label: "Goal (e.g. re-engage after open no click)" }, { k: "tone", label: "Tone" }] },
  { key: "send_email_ab", label: "Send email — A/B variants", fields: [], ab: true },
  { key: "send_sms", label: "Send SMS (Twilio)", fields: [{ k: "content", label: "SMS text", ta: true }] },
];

const CONDITION_FIELDS = [
  { key: "tags", label: "Contact tags", ops: ["contains", "not_contains"] },
  { key: "status", label: "Status", ops: ["equals", "not_equals"] },
  { key: "score", label: "AI score", ops: ["gte", "lte", "equals"] },
  { key: "company", label: "Company", ops: ["contains", "not_contains", "equals"] },
  { key: "source", label: "Source", ops: ["equals", "not_equals", "contains"] },
];

export default function AutomationEditor() {
  const { id } = useParams();
  const [wf, setWf] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/workflows/${id}`).then(({ data }) => setWf(data)).catch(() => navigate("/automations"));
  }, [id, navigate]);

  if (!wf) return <div className="p-10 section-label">Loading…</div>;

  const trigDef = TRIGGERS.find((t) => t.key === wf.trigger?.type);
  const setTriggerType = (type) => setWf({ ...wf, trigger: { type, filters: {} } });
  const setFilter = (k, v) => setWf({ ...wf, trigger: { ...wf.trigger, filters: { ...(wf.trigger.filters || {}), [k]: v } } });

  const addAction = (type) => {
    setWf({ ...wf, actions: [...(wf.actions || []), { type, config: {}, wait_before_minutes: 0, condition: null }] });
  };
  const removeAction = (i) => setWf({ ...wf, actions: wf.actions.filter((_, x) => x !== i) });
  const setActionField = (i, k, v) => {
    const arr = [...wf.actions];
    arr[i] = { ...arr[i], config: { ...(arr[i].config || {}), [k]: v } };
    setWf({ ...wf, actions: arr });
  };
  const setActionMeta = (i, k, v) => {
    const arr = [...wf.actions];
    arr[i] = { ...arr[i], [k]: v };
    setWf({ ...wf, actions: arr });
  };
  const setActionCondition = (i, patch) => {
    const arr = [...wf.actions];
    const c = arr[i].condition || { field: "tags", op: "contains", value: "" };
    arr[i] = { ...arr[i], condition: { ...c, ...patch } };
    setWf({ ...wf, actions: arr });
  };
  const toggleCondition = (i) => {
    const arr = [...wf.actions];
    arr[i] = { ...arr[i], condition: arr[i].condition ? null : { field: "tags", op: "contains", value: "" } };
    setWf({ ...wf, actions: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/workflows/${id}`, wf);
      setWf(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="automation-editor">
      <Link to="/automations" data-testid="back-automations" className="section-label inline-flex items-center gap-1 hover:text-[var(--peak-accent)]"><ArrowLeft className="w-3 h-3" /> Back</Link>

      <div className="flex items-end justify-between mt-2 mb-6 flex-wrap gap-4">
        <div className="flex-1">
          <div className="section-label">Workflow</div>
          <input
            data-testid="wf-name"
            value={wf.name}
            onChange={(e) => setWf({ ...wf, name: e.target.value })}
            className="brutalist-input font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase mt-1"
          />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch data-testid="wf-enabled" checked={wf.enabled} onCheckedChange={(v) => setWf({ ...wf, enabled: v })} />
            <span className="section-label">{wf.enabled ? "Enabled" : "Draft"}</span>
          </div>
          <button data-testid="wf-save" onClick={save} disabled={saving} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="brutalist-card p-6">
          <div className="section-label">Trigger</div>
          <div className="font-heading font-bold text-xl mt-1 mb-3">When…</div>
          <div className="grid grid-cols-1 gap-2">
            {TRIGGERS.map((t) => (
              <button
                key={t.key}
                data-testid={`trig-${t.key}`}
                onClick={() => setTriggerType(t.key)}
                className={`text-left px-3 py-2 border text-sm font-bold uppercase tracking-wider ${wf.trigger?.type === t.key ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] hover:border-[var(--peak-ink)]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {trigDef?.filters?.length > 0 && (
            <div className="mt-4 space-y-3">
              {trigDef.filters.map((f) => (
                <div key={f.k}>
                  <label className="section-label">{f.label}</label>
                  <input
                    data-testid={`trig-filter-${f.k}`}
                    className="brutalist-input"
                    value={wf.trigger?.filters?.[f.k] || ""}
                    onChange={(e) => setFilter(f.k, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="brutalist-card p-6">
          <div className="section-label">Actions</div>
          <div className="font-heading font-bold text-xl mt-1 mb-3">Then…</div>

          <div className="space-y-3">
            {(wf.actions || []).map((a, i) => {
              const def = ACTIONS.find((x) => x.key === a.type);
              const condField = CONDITION_FIELDS.find((f) => f.key === a.condition?.field);
              return (
                <div key={i} className="border border-[var(--peak-border)] p-3" data-testid={`action-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-widest">Step {i + 1} · {def?.label || a.type}</div>
                    <button onClick={() => removeAction(i)} data-testid={`remove-action-${i}`} className="hover:text-[#E50000]"><X className="w-3.5 h-3.5" /></button>
                  </div>

                  {/* Wait before */}
                  <div className="mb-3 flex items-center gap-2">
                    <label className="section-label whitespace-nowrap">Wait before (min)</label>
                    <input
                      type="number"
                      min="0"
                      data-testid={`action-${i}-wait`}
                      className="brutalist-input w-20"
                      value={a.wait_before_minutes || 0}
                      onChange={(e) => setActionMeta(i, "wait_before_minutes", Math.max(0, parseInt(e.target.value) || 0))}
                    />
                    {a.wait_before_minutes > 0 && (
                      <span className="text-[10px] font-mono text-[var(--peak-muted)]">= {a.wait_before_minutes >= 60 ? `${Math.round(a.wait_before_minutes/60*10)/10}h` : `${a.wait_before_minutes}m`}</span>
                    )}
                  </div>

                  {def?.fields.map((f) => (
                    <div key={f.k} className="mb-2">
                      <label className="section-label">{f.label}</label>
                      {f.ta ? (
                        <textarea rows={2} data-testid={`action-${i}-${f.k}`} className="w-full border border-[var(--peak-border)] p-2 text-sm focus:outline-none focus:border-[var(--peak-ink)]" value={a.config?.[f.k] || ""} onChange={(e) => setActionField(i, f.k, e.target.value)} />
                      ) : (
                        <input data-testid={`action-${i}-${f.k}`} className="brutalist-input" value={a.config?.[f.k] || ""} onChange={(e) => setActionField(i, f.k, e.target.value)} />
                      )}
                    </div>
                  ))}

                  {def?.ab && (
                    <ABVariantsEditor
                      variants={a.config?.variants || []}
                      onChange={(variants) => setActionField(i, "variants", variants)}
                      testPrefix={`action-${i}-ab`}
                    />
                  )}

                  {/* Condition */}
                  <div className="mt-3 pt-3 border-t border-[var(--peak-border)]">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" data-testid={`action-${i}-cond-toggle`} checked={!!a.condition} onChange={() => toggleCondition(i)} />
                      <span className="section-label">Only run if…</span>
                    </label>
                    {a.condition && (
                      <div className="grid grid-cols-3 gap-2 mt-2" data-testid={`action-${i}-cond`}>
                        <select data-testid={`action-${i}-cond-field`} className="brutalist-input" value={a.condition.field} onChange={(e) => setActionCondition(i, { field: e.target.value, op: (CONDITION_FIELDS.find(f => f.key === e.target.value)?.ops || ["contains"])[0] })}>
                          {CONDITION_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <select data-testid={`action-${i}-cond-op`} className="brutalist-input" value={a.condition.op} onChange={(e) => setActionCondition(i, { op: e.target.value })}>
                          {(condField?.ops || ["contains"]).map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input data-testid={`action-${i}-cond-value`} className="brutalist-input" value={a.condition.value || ""} onChange={(e) => setActionCondition(i, { value: e.target.value })} placeholder="value" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <div className="section-label mb-2">Add action</div>
            <div className="grid grid-cols-2 gap-2">
              {ACTIONS.map((a) => (
                <button key={a.key} data-testid={`add-action-${a.key}`} onClick={() => addAction(a.key)} className="border border-[var(--peak-border)] hover:border-[var(--peak-ink)] p-2 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" /> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ABVariantsEditor({ variants, onChange, testPrefix }) {
  const list = variants && variants.length > 0
    ? variants
    : [
        { name: "A", subject: "", body: "", weight: 1 },
        { name: "B", subject: "", body: "", weight: 1 },
      ];

  const setV = (i, patch) => {
    const arr = list.map((v, x) => x === i ? { ...v, ...patch } : v);
    onChange(arr);
  };
  const remove = (i) => onChange(list.filter((_, x) => x !== i));
  const add = () => {
    const nextLetter = String.fromCharCode(65 + list.length); // A, B, C…
    onChange([...list, { name: nextLetter, subject: "", body: "", weight: 1 }]);
  };

  return (
    <div className="border-t border-[var(--peak-border)] mt-2 pt-2" data-testid={testPrefix}>
      <div className="section-label mb-2">A/B variants ({list.length})</div>
      <div className="space-y-3">
        {list.map((v, i) => (
          <div key={i} className="border border-[var(--peak-border)] p-3 bg-[var(--peak-bg)]" data-testid={`${testPrefix}-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <input
                data-testid={`${testPrefix}-${i}-name`}
                className="brutalist-input flex-1"
                placeholder={`Variant ${String.fromCharCode(65 + i)}`}
                value={v.name || ""}
                onChange={(e) => setV(i, { name: e.target.value })}
              />
              <label className="section-label whitespace-nowrap">weight</label>
              <input
                type="number"
                min="1"
                data-testid={`${testPrefix}-${i}-weight`}
                className="brutalist-input w-16"
                value={v.weight || 1}
                onChange={(e) => setV(i, { weight: Math.max(1, parseInt(e.target.value) || 1) })}
              />
              {list.length > 2 && (
                <button
                  onClick={() => remove(i)}
                  data-testid={`${testPrefix}-${i}-remove`}
                  className="text-[var(--peak-muted)] hover:text-[#E50000] px-1"
                >
                  ×
                </button>
              )}
            </div>
            <input
              data-testid={`${testPrefix}-${i}-subject`}
              className="brutalist-input"
              placeholder="Subject"
              value={v.subject || ""}
              onChange={(e) => setV(i, { subject: e.target.value })}
            />
            <textarea
              rows={2}
              data-testid={`${testPrefix}-${i}-body`}
              className="w-full border border-[var(--peak-border)] p-2 text-sm focus:outline-none focus:border-[var(--peak-ink)] mt-2"
              placeholder="Body"
              value={v.body || ""}
              onChange={(e) => setV(i, { body: e.target.value })}
            />
          </div>
        ))}
      </div>
      <button
        onClick={add}
        data-testid={`${testPrefix}-add`}
        className="mt-3 border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-3 py-1.5"
      >
        + Add variant
      </button>
    </div>
  );
}

